import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { WorkspaceRole } from "@/config/roles";
import { useAuth } from "@/contexts/AuthContext";
import { useOrg } from "@/contexts/OrgContext";
import { supabase } from "@/integrations/supabase/client";

export type { WorkspaceRole } from "@/config/roles";

export interface WorkspaceConfig {
  key: WorkspaceRole;
  label: string;
  shortLabel: string;
  icon: string;
  description: string;
  color: string;
}

export const WORKSPACE_CONFIGS: WorkspaceConfig[] = [
  {
    key: "executive_director",
    label: "Executive Director",
    shortLabel: "Executive",
    icon: "Crown",
    description: "Leadership Command Center",
    color: "text-amber-600",
  },
  {
    key: "membership_pro",
    label: "Membership Pro",
    shortLabel: "Membership",
    icon: "Users",
    description: "Membership Growth Center",
    color: "text-emerald-600",
  },
  {
    key: "event_planner",
    label: "Event Planner",
    shortLabel: "Events",
    icon: "CalendarDays",
    description: "Events Operations Center",
    color: "text-blue-600",
  },
  {
    key: "marketing_comms",
    label: "Marketing & Comms",
    shortLabel: "Marketing",
    icon: "Megaphone",
    description: "Audience & Campaign Center",
    color: "text-purple-600",
  },
];

const STORAGE_KEY = "memberos_last_workspace";

/**
 * Fetches workspace roles via the get_user_workspace_roles RPC
 * which reads from organization_users.
 */
export const useWorkspaceRoles = () => {
  const { user } = useAuth();
  const { orgId } = useOrg();

  return useQuery({
    queryKey: ["workspace_roles", user?.id, orgId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_user_workspace_roles", {
        _user_id: user!.id,
        _org_id: orgId!,
      });
      if (error) throw error;
      return ((data || []) as string[]).filter(Boolean) as WorkspaceRole[];
    },
    enabled: !!user && !!orgId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useWorkspace = () => {
  const { data: roles = [], isLoading } = useWorkspaceRoles();

  const [activeWorkspace, setActiveWorkspaceState] = useState<WorkspaceRole | null>(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored as WorkspaceRole | null;
  });

  useEffect(() => {
    if (isLoading || roles.length === 0) return;
    if (activeWorkspace && roles.includes(activeWorkspace)) return;
    const stored = sessionStorage.getItem(STORAGE_KEY) as WorkspaceRole | null;
    if (stored && roles.includes(stored)) {
      setActiveWorkspaceState(stored);
    } else {
      setActiveWorkspaceState(roles[0]);
      sessionStorage.setItem(STORAGE_KEY, roles[0]);
    }
  }, [roles, isLoading, activeWorkspace]);

  const setActiveWorkspace = useCallback((role: WorkspaceRole) => {
    setActiveWorkspaceState(role);
    sessionStorage.setItem(STORAGE_KEY, role);
  }, []);

  const activeConfig = useMemo(
    () => WORKSPACE_CONFIGS.find((c) => c.key === activeWorkspace) || null,
    [activeWorkspace],
  );

  const availableWorkspaces = useMemo(() => WORKSPACE_CONFIGS.filter((c) => roles.includes(c.key)), [roles]);

  return {
    roles,
    activeWorkspace,
    activeConfig,
    availableWorkspaces,
    setActiveWorkspace,
    hasWorkspaces: roles.length > 0,
    isLoading,
  };
};
